import { useEffect, useRef } from "react";

// Small modal shell shared across Settings flows (revoke confirm, delete-
// account confirm, one-time-secret reveal). Renders a fixed full-screen
// backdrop with a centered ink-900 panel; closes on Escape and on
// backdrop click unless `dismissible={false}` is passed (the one-time
// secret panel is non-dismissible — the user MUST click the explicit
// "I've saved it" button so we never silently lose the only copy of a
// freshly-minted token).
//
// Intentionally NOT a portal: the SPA shell has no z-stacked layers
// today, so a top-level fixed overlay sits above everything and we
// avoid the React-tree / focus-trap complexity that comes with a real
// portal until a second modal-bearing surface forces it.
export function Modal({
  open,
  onClose,
  title,
  children,
  dismissible = true,
  width = "max-w-md",
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  width?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismissible, onClose]);

  // Lock background scroll while the modal is open so the page behind
  // doesn't visibly drift when the user wheels over the backdrop.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center px-4"
      onMouseDown={(e) => {
        if (!dismissible) return;
        // Only close when the click started on the backdrop itself; clicks
        // that started inside the panel and dragged out (e.g. text-select)
        // shouldn't dismiss.
        if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`${width} w-full bg-ink-900 border border-ink-800 rounded-md p-6 animate-fade-in`}
      >
        {title ? (
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3">
            {title}
          </h3>
        ) : null}
        {children}
      </div>
    </div>
  );
}
